import {
  CHART_AXIS_STROKE,
  CHART_COLOR_PALETTE,
  CHART_GRID_STROKE,
  CHART_TICK_COLOR,
} from "src/components/charts/ChartPalette";
import { getNumberLocale } from "src/utils/numberFormat";
import { formatPaidMediaDateText } from "../../utils/PaidMediaNumberFormat";
import type {
  PaidMediaOverviewGranularity,
  PaidMediaOverviewPlatform,
} from "src/services/PaidMediaOverviewAPI";

export const overviewAxisProps = {
  stroke: CHART_AXIS_STROKE,
  tick: { fill: CHART_TICK_COLOR, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: CHART_AXIS_STROKE },
};

export function formatOverviewShortDate(date: string): string {
  const [, month, day] = date.split("-");
  const formatted = month && day ? `${month}/${day}` : date;
  return formatPaidMediaDateText(formatted);
}

/**
 * Axis label for a `trends` bucket. Daily buckets stay as MM/DD; weekly and
 * monthly buckets are localised, so every chart reading the same series shows
 * the same tick text.
 */
export function formatOverviewBucketLabel(
  bucketDate: string,
  granularity: PaidMediaOverviewGranularity,
  weekLabel: (_date: string) => string,
  locale: string
): string {
  if (!bucketDate) {
    return "";
  }

  if (granularity === "daily") {
    return formatOverviewShortDate(bucketDate);
  }

  const parsed = new Date(bucketDate.length === 7 ? `${bucketDate}-01` : bucketDate);
  if (Number.isNaN(parsed.getTime())) {
    return formatPaidMediaDateText(bucketDate);
  }

  const intlLocale = getNumberLocale(locale);

  if (granularity === "monthly") {
    return parsed.toLocaleDateString(intlLocale, { month: "short", year: "2-digit" });
  }

  return weekLabel(parsed.toLocaleDateString(intlLocale, { month: "short", day: "numeric" }));
}


export const OVERVIEW_META_COLOR = CHART_COLOR_PALETTE[0]; // #2b74e4
export const OVERVIEW_GOOGLE_COLOR = CHART_COLOR_PALETTE[2]; // #f6c15f
export const OVERVIEW_META_REVENUE_COLOR = CHART_COLOR_PALETTE[4]; // #5d97ee
export const OVERVIEW_GOOGLE_REVENUE_COLOR = CHART_COLOR_PALETTE[6]; // #f0a84a
export const OVERVIEW_BENCHMARK_COLOR = CHART_COLOR_PALETTE[3]; // #8d4fd3
export const OVERVIEW_GRID_COLOR = CHART_GRID_STROKE;
export const OVERVIEW_SPIKE_COLOR = "#ff6c00";
export const OVERVIEW_REFERENCE_LINE_COLOR = "#cbd5e1";
export const OVERVIEW_REFERENCE_LABEL_COLOR = "#64748b";
export const OVERVIEW_AXIS_LABEL_COLOR = CHART_TICK_COLOR;

export const OVERVIEW_PLATFORM_COLOR: Record<PaidMediaOverviewPlatform, string> = {
  meta_ads: OVERVIEW_META_COLOR,
  google_ads: OVERVIEW_GOOGLE_COLOR,
};

/*
 * Fallback for a `platform` value that isn't in the map above. Deliberately
 * neutral rather than either brand colour: defaulting an unrecognised platform
 * to Meta's blue makes a mixed chart read as if it were entirely Meta, which
 * hides the mismatch instead of surfacing it.
 */
export const OVERVIEW_UNKNOWN_PLATFORM_COLOR = "#94a3b8";

/*
 * Status-badge and platform-dot colours are deliberately NOT declared here.
 * Those render as DOM elements, so they live in styles/_badges.scss keyed by the
 * BEM modifier from `overviewStatusModifier`. Only values Recharts needs as SVG
 * presentation attributes belong in this file.
 */
